<template>
<div class="profiletable__row flex w-full">
  <div class="profiletable__row__data flex-none w-150" :class="bgColor">
    <AttributeIcon :size="25" :attributeKey="attributeKey"></AttributeIcon>
    <div class="font-serif text-lg leading-snug">{{ attributeTitle }}</div>
    <div class="font-sans-serif text-sm">{{ attributeValue }}</div>
  </div>
  <div class="profiletable__row__data flex-auto w-full bg--gray-200">
    <div v-for="(category, c_key) of incidentCategories(incidents)" :key="c_key">
      <div :class="{ isOpen : categoryIsOpen(category) && category !== '', isClosed : !categoryIsOpen(category) }">
        <div v-if="category !== ''">
          <span class="mt-0 cursor-pointer mb-10 text-bold">
            {{ category.title }}:
          </span>
          <span>
            <span v-if="!categoryIsOpen(category)">
              <span v-html="categoryExcerpt(category, incidents)"></span>
              <MiniSourceLink :sources="categoryExcerptSources(category, incidents)"></MiniSourceLink>
            </span>
            <span
                @click="toggleCategory(category)"
                v-if="incidentsInCategory(category, incidents).length > 1 && category !== ''"
              >
                <span class="cursor-pointer">
                  (<span v-if="!categoryIsOpen(category)" class="underline text-bold color--blue">Read More</span>
                    <span class="underline text-bold color--blue" v-else>...</span>)
                </span>
              </span>
          </span>
        </div>
        <ul v-if="categoryIsOpen(category)" class="pl-20 mt-0 mb-20">
          <li v-for="incident of incidentsInCategory(category, incidents)" :key="incident.id">
            <span v-html="incident.incident_report"></span><br>
            <MiniSourceLink :sources="incident.sources"></MiniSourceLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div class="profiletable__row__data bg--gray-lightest flex-none w-100 text-center">
    <DevelopmentsToWatch
      class="barchart__line__icon flex-none"
      v-if="$store.getters['covid19/countryAttributeType'](country.id, attributeKey) === 'watch'"
      :shadow="false"
      :size="45"
    ></DevelopmentsToWatch>

    <ConcerningDevelopments
    class="barchart__line__icon flex-none"
      v-if="$store.getters['covid19/countryAttributeType'](country.id, attributeKey) === 'high'"
      :shadow="false"
      :size="45"
    ></ConcerningDevelopments>
  </div>
</div>
</template>

<script>
import _ from 'lodash'
import Vue from 'vue'
import AttributeIcon from './../shared/icons/AttributeIcon.vue'
import DevelopmentsToWatch from './../shared/icons/DevelopmentsToWatch.vue'
import ConcerningDevelopments from './../shared/icons/ConcerningDevelopments.vue'
import MiniSourceLink from './MiniSourceLink.vue'

export default {
  'name': 'profile-table-row',
  components: {
    AttributeIcon,
    DevelopmentsToWatch,
    ConcerningDevelopments,
    MiniSourceLink
  },
  'props': {
    'attribute-key': {
      type: String,
      required: false
    },
    dataset: {
      required: true
    },
    covid19dataset: {
      required: true
    },
    country: {
      required: true
    }
  },
  data () {
    return {
      openCategories: []
    }
  },
  computed: {
    bgColor () {
      let color = Vue.filter('valueColor')(this.attributeValue)
      return ['bg--' + color]
    },
    incidents () {
      let result = []

      if (!this.covid19dataset || !this.covid19dataset.incident_reports) {
        return result
      }

      _.each(this.covid19dataset.incident_reports, (item) => {
        if (item.attribute_code === this.attributeKey) {
          result.push(item)
        }
      })

      return result
    },
    attributeValue () {
      return this.dataset && this.dataset[this.attributeKey] ? Math.round(this.dataset[this.attributeKey] * 100) / 100 : ''
    },
    attributeTitle () {
      let label = this.$store.getters['attributes/getLabelFromKey'](this.attributeKey)
      return label ? label.name : ''
    }
  },
  methods: {
    /**
     * Check if a list of incident has any reports not attached to a category
     *
     * @param   {array}  incidents
     *
     * @return  {boolean}
     */
    hasEmptyCategories (incidents) {
      for (const incident of incidents) {
        if (!incident.category || !incident.category.title) {
          return true
        }
      }
    },

    /**
     * Return all categories available in an array of inicident reports
     *
     * @param   {array}  incidents
     *
     * @return  {array}
     */
    incidentCategories (incidents) {
      let result = []

      for (const incident of incidents) {
        if (incident.category && incident.category.title && !_.find(result, { 'id': incident.category.id })) {
          result.push(incident.category)
        }
      }

      if (this.hasEmptyCategories(incidents)) {
        result.push('')
      }

      return result
    },

    /**
     * Return the incidents from a category
     *
     * @param   {obj}  category   The category
     * @param   {array}  incidents  Array of all incidents
     *
     * @return  {array}
     */
    incidentsInCategory (category, incidents) {
      let result = []
      for (const incident of incidents) {
        if (incident.category && incident.category.title && incident.category.id === category.id) {
          result.push(incident)
        } else if (!incident.category || !incident.category.title && category === '') {
          result.push(incident)
        }
      }
      result.sort((a, b) => { if (a.weight < b.weight) { return -1 } else if (a.weight > b.weight) { return 1 } return 0 })
      return result
    },

    /**
     * Toggle a category open/clised
     *
     * @param   {obj}  category
     *
     * @return  {void}
     */
    toggleCategory (category) {
      if (this.categoryIsOpen(category)) {
        let index = _.findIndex(this.openCategories, (cat) => {
          return parseInt(cat.id) === parseInt(category.id)
        })
        this.openCategories.splice(index, 1)
      } else {
        this.openCategories.push(category)
      }
    },

    /**
     * Check if the category is open
     *
     * @param   {obj}  category  The category
     *
     * @return  {boolean}
     */
    categoryIsOpen (category) {
      if (category === '' || !category.id) {
        return true
      }

      return !!_.find(this.openCategories, {'id': category.id})
    },

    /**
     * Get an excerpt incident from a category
     *
     * @param   {obj}  category
     * @param   {array}  incidents  Incidents
     *
     * @return  {string}
     */
    categoryExcerptIncident (category, incidents) {
      let items = this.incidentsInCategory(category, incidents)
      let incident = _.first(items)

      if (incident && incident.incident_report) {
        return incident
      }
    },

    /**
     * Get the excerpt from a category
     *
     * @param   {obj}  category
     * @param   {array}  incidents  Incidents
     *
     * @return  {string}
     */
    categoryExcerpt (category, incidents) {
      let txt = ''
      let incident = this.categoryExcerptIncident(category, incidents)

      if (incident) {
        txt += incident.incident_report
        if (this.incidentsInCategory(category, incidents).length > 1) {
          txt += ' ...'
        }
      }

      return txt
    },

    /**
     * Get the excerpt sources from a category
     *
     * @param   {obj}  category
     * @param   {array}  incidents  Incidents
     *
     * @return  {obj}
     */
    categoryExcerptSources (category, incidents) {
      let incident = this.categoryExcerptIncident(category, incidents)

      if (incident && incident.sources) {
        return incident.sources
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.isOpen {
  border-bottom: 3px solid #EEE;
  margin-bottom: 20px;
  padding: 0;
}

.isClosed {
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/covid19/ProfiletableRow.vue